import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { MessageService } from 'primeng/api';
import { Observable, Subject, catchError } from 'rxjs';
import { RedirectRequest } from '@azure/msal-browser';
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';

import { environment } from 'src/environments/environment.development';
import { ErrorHandlerService } from './error-handler.service';
import { AUTH_MESSAGES, AUTH_STRINGS } from 'src/app/shared/constants/app-strings.constants';
import { Login } from 'src/app/shared/models/login.model';
import { SSOLogin } from 'src/app/shared/models/sso-login.model';
import { LoginResponse } from 'src/app/shared/models/login-response.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  loginLogoutStatus$ = new Subject<void>();
  private loginSsoUrl = `${environment.LOGIN_SSO_URL}`;

  constructor(
    private http: HttpClient,
    private messageService: MessageService,
    private errorHandlerService: ErrorHandlerService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration, 
    private msalService: MsalService
  ) {}

  login(credentials: Login): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(
        `${environment.API_URL}/wgassessmentlogin/login`,
        credentials,
        {
          headers: new HttpHeaders({ 'X-Skip-AuthInterceptor': '' }),
        }
      )
      .pipe(catchError((error: HttpErrorResponse) => this.errorHandlerService.handleError(error)));
  }

  logout(): void {
    sessionStorage.removeItem('msal-access-token');
    sessionStorage.removeItem('msal-id-token');
    sessionStorage.removeItem(AUTH_STRINGS.idtoken);
    this.messageService.add({
      severity: AUTH_MESSAGES.success,
      detail: AUTH_MESSAGES.logoutSuccess,
    });
    this.loginLogoutStatus$.next();
  }

  getIdToken(): string | null {
    const idtoken = sessionStorage.getItem(AUTH_STRINGS.idtoken);
    return idtoken;
  }

  getLoginStatus(): boolean {
    const idtoken = this.getIdToken();
    if (!idtoken) return false;
    return true;
  }

  isMsalLoggedIn(): boolean {
    const msalAccessToken = sessionStorage.getItem('msal-access-token');
    const msalIdToken = sessionStorage.getItem('msal-id-token');

    if (msalAccessToken && msalIdToken) {
      try {
        const tokenData = JSON.parse(msalAccessToken);
        if (tokenData.expiry) {
          const expiryTime = new Date(tokenData.expiry).getTime();
          const now = new Date().getTime();
          return expiryTime > now;
        }
        return false;
      }
      catch {
        return false;
      }
    }
    return false;
  }

  loginSso(): void {
    if (this.msalGuardConfig.authRequest) {
        this.msalService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
    } else {
        this.msalService.loginRedirect();
    }
  }

  loginUsingSso(loginData: SSOLogin): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(this.loginSsoUrl, loginData);
  }
}
